//-- Copyright (c) 2021 TMT Insights Group, LLC
import { parseDate } from '@/_utils/date';
import { createSelector } from '@reduxjs/toolkit';
import { isAfter } from 'date-fns';
import { LoadingStatuses } from '../../constants/constants';
import { DashboardState } from '../slices/slice_types';
import { RootState } from '../store';

export const selectAllDashboardData = (state: RootState): DashboardState['data'] => state.dashboard?.data || {};
export const selectDailyReportData = (state: RootState): DashboardState['dailyReportData'] => state.dashboard?.dailyReportData || {};
export const selectGeneratedReportData = (state: RootState): DashboardState['generatedReportData'] => state.dashboard?.generatedReportData || {};
export const selectCurrentCycle = (state: RootState): DashboardState['currentCycle'] => state.dashboard?.currentCycle || 'All';
export const selectDashboardIsLoading = (state: RootState): boolean => state.dashboard?.status === LoadingStatuses.pending;
export const selectDashboardLoadingState = (state: RootState): DashboardState['status'] => state.dashboard?.status;
export const selectDailyReportStatus = (state: RootState): DashboardState['dailyReportStatus'] => state.dashboard?.dailyReportStatus;
export const selectGeneratedReportStatus = (state: RootState): DashboardState['dailyReportStatus'] => state.dashboard?.generatedReportStatus;

export const selectGraphsStatus = (state: RootState): DashboardState['graphs']['status'] => state.dashboard?.graphs.status || false;
export const selectGraphsReadOnly = (state: RootState): DashboardState['graphs']['readOnly'] => state.dashboard?.graphs.readOnly || {};
export const selectGraphsEditable = (state: RootState): DashboardState['graphs']['editable'] => state.dashboard?.graphs.editable || {};
export const selectGraphsRecentTasks = (state: RootState): DashboardState['graphs']['recentTasks'] => state.dashboard?.graphs.recentTasks || {};

export const selectDashboardData = (state: RootState): DashboardState['data'] => {
  const cycleDate = selectCurrentCycle(state);
  const data = selectAllDashboardData(state);
  if (cycleDate !== 'All' && typeof cycleDate !== 'object') {
    return data[cycleDate as keyof typeof data];
  }
  return data;
};

const ignoreKeys = ['otd70', 'otd100', 'Awaiting Submit'];
export const selectStatusTotal = (state: RootState, field: string): number => {
  const data = selectDashboardData(state);
  const cycleDate = selectCurrentCycle(state);
  if (cycleDate !== 'All' && typeof cycleDate !== 'object') {
    return data[field as keyof typeof data] || 0;
  } else {
    const total = Object.keys(data).reduce((total, key) => {
      if (ignoreKeys.includes(key) || !data[key as keyof typeof data]?.[field]) return total;
      return total + data[key as keyof typeof data][field];
    }, 0);
    return total || 0;
  }
};

// export const selectDailyReports = (state: RootState) => state?.dashboard?.dailyReports || [];

// export const selectNotifications = (state: RootState) => state?.dashboard?.notifications || [];

export const selectCycleDates = createSelector([selectAllDashboardData], (allDashboardData) => {
  const filteredCycleDates = Object.keys(allDashboardData)
    .filter((date) => isAfter(parseDate(date), new Date('2021-01-01')))
    .sort((date1, date2) => {
      const dateA = new Date(date1);
      const dateB = new Date(date2);
      if (dateA < dateB) {
        return 1;
      } else if (dateA > dateB) {
        return -1;
      } else {
        return 0;
      }
    });

  return filteredCycleDates;
});

export const isGraphSquished = (graphId: string) => {
  return createSelector(selectGraphsReadOnly, (readOnly) => {
    const rowItsIn = readOnly.order.filter((row) => row.includes(graphId));
    return rowItsIn.length > 0 ? rowItsIn[0].length >= 3 : false;
  });
};

export const isGraphAlone = (graphId: string) => {
  return createSelector(selectGraphsReadOnly, (readOnly) => {
    const rowItsIn = readOnly.order.filter((row) => row.includes(graphId));
    return rowItsIn.length > 0 ? rowItsIn[0].length === 1 : false;
  });
};
